import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const contact = siteConfig.contact || {};

  useEffect(() => {
    document.title = `Contact — ${siteConfig.brandName}`;
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));

    if (errors[name]) {
      setErrors((previous) => ({ ...previous, [name]: "" }));
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.subject) {
      newErrors.subject = "Please select a subject";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    return newErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    window.setTimeout(() => {
      setShowSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.setTimeout(() => setShowSuccess(false), 5000);
    }, 1000);
  };

  const fieldError = (name) =>
    errors[name] ? (
      <span className="contact-field__error" id={`${name}-error`}>
        {errors[name]}
      </span>
    ) : null;

  return (
    <main className="contact-page" id="main-content">
      <section className="contact-hero" aria-labelledby="contact-title">
        <p className="contact-eyebrow">Let’s talk paper</p>
        <h1 id="contact-title">We would love to hear from you.</h1>
        <p>
          Questions, kind words and wonderfully specific card requests are all
          welcome. Send us a note and our small team will be in touch soon.
        </p>
      </section>

      {showSuccess && (
        <div className="contact-success" role="status" aria-live="polite">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Your note is on its way.</strong>
            <p>
              Thanks for reaching out. We will reply within 1–2 business days.
            </p>
          </div>
        </div>
      )}

      <section
        className="contact-layout"
        aria-label="Contact information and form"
      >
        <aside className="contact-details">
          <p className="contact-eyebrow">Here to help</p>
          <h2>A real person reads every note.</h2>
          <p className="contact-details__intro">
            Before writing, you may find a quick answer in our shipping and
            returns guides.
          </p>

          <div className="contact-quick-links">
            <Link to="/shipping">
              Shipping guide <span>→</span>
            </Link>
            <Link to="/returns">
              Returns guide <span>→</span>
            </Link>
          </div>

          <dl className="contact-list">
            {contact.email && (
              <div>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </dd>
              </div>
            )}

            {contact.address && (
              <div>
                <dt>Studio</dt>
                <dd>{contact.address}</dd>
              </div>
            )}
            <div>
              <dt>Response time</dt>
              <dd>Monday–Friday, within 1–2 business days</dd>
            </div>
          </dl>
        </aside>

        <div className="contact-form-card">
          <p className="contact-eyebrow">Send a note</p>
          <h2>What can we help with?</h2>
          <form noValidate onSubmit={handleSubmit}>
            <div className="contact-form__row">
              <div className="contact-field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={
                    errors.firstName ? "firstName-error" : undefined
                  }
                />
                {fieldError("firstName")}
              </div>
              <div className="contact-field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={
                    errors.lastName ? "lastName-error" : undefined
                  }
                />
                {fieldError("lastName")}
              </div>
            </div>

            <div className="contact-form__row">
              <div className="contact-field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {fieldError("email")}
              </div>
              <div className="contact-field">
                <label htmlFor="phone">
                  Phone <span>(optional)</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="contact-field">
              <label htmlFor="subject">How can we help? *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              >
                <option value="">Choose a subject</option>
                <option value="order">An existing order</option>
                <option value="product">A product question</option>
                <option value="shipping">Shipping or delivery</option>
                <option value="returns">Returns or exchanges</option>
                <option value="wholesale">Wholesale or press</option>
                <option value="other">Something else</option>
              </select>
              {fieldError("subject")}
            </div>

            <div className="contact-field">
              <label htmlFor="message">Your message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
                placeholder="Order number, product name, or anything else that will help us answer..."
              />
              {fieldError("message")}
            </div>

            <button
              className="contact-submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending your note…" : "Send message"}
            </button>
            <p className="contact-form__privacy">
              By submitting this form, you agree to our{" "}
              <Link to="/privacy">privacy policy</Link>.
            </p>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
